import {
  WizardBottomBar,
  WizardBottomBarPrimaryButton,
  WizardBottomBarSection,
  WizardBottomBarSpacer,
  WizardContentBody,
  WizardContentHeader,
  WizardContentHeaderDescription,
  WizardContentHeaderTitle,
  WizardPageAnimatedContent,
  WizardPageSingleColumnRow,
} from '@/components/Wizard'
import styles from '../MeasureOne.module.css'
import { lazy, Suspense } from 'react'
import { useOutletContext } from 'react-router'
import type { JobDetailsViewModel } from '@/features/jobs/details/JobDetailsViewModel'
import { links } from '@/utils/link'
import type { TaxJobDetailsViewModel } from '@/features/jobs/details/tax-type/TaxJobDetailsViewModel'
import { MeasureOneKicker } from '@/features/jobs/details/tax-type/info-gathering/measure-one/components/MeasureOneKicker'
import { observer } from 'mobx-react-lite'
import { Id } from '@/infra/id'
import env from '@/infra/env'
import { ButtonGroup } from '@/components/ButtonGroup'
import { Button } from '@/components/Button'
import { MeasureOneSpinner } from '@/features/jobs/details/tax-type/info-gathering/measure-one/components/MeasureOneSpinnner'

const loadMeasureOneWidget = () => {
  return new Promise<void>((resolve, reject) => {
    if (customElements.get('m1-link')) {
      return resolve()
    }

    const script = document.createElement('script')
    script.src = `https://${env.MEASUREONE_HOST_NAME}/v3/js/m1-link-2021042000.js`
    script.async = true
    script.onload = () => resolve()
    script.onerror = reject
    document.body.appendChild(script)
  })
}

const MeasureOneWidget = lazy(async () => {
  await loadMeasureOneWidget()
  return import('../../components/MeasureOneWidget').then((module) => ({
    default: module.MeasureOneWidget,
  }))
})

export default observer(function MeasureOneConnectionContainer() {
  const vm = useOutletContext<JobDetailsViewModel>()
  const taxVm = vm.typeVm as TaxJobDetailsViewModel
  const measureOneVm = taxVm.infoGatheringVm.measureOneVm

  return (
    <>
      <WizardPageSingleColumnRow>
        <WizardPageAnimatedContent
          animationKey="default"
          animationDirection="forward"
        >
          <div className={styles.content}>
            <WizardContentHeader>
              <MeasureOneKicker />

              <WizardContentHeaderTitle>
                Setting up your connection
              </WizardContentHeaderTitle>
              <WizardContentHeaderDescription>
                Use the MeasureOne flow to search for your provider, enter your
                credentials, and finish the setup
              </WizardContentHeaderDescription>
            </WizardContentHeader>
          </div>
        </WizardPageAnimatedContent>
      </WizardPageSingleColumnRow>

      <WizardPageSingleColumnRow>
        <WizardPageAnimatedContent
          animationKey="default"
          animationDirection="forward"
        >
          <WizardContentBody>
            <ConnectionBody
              jobId={Id.assert(vm.job.id)}
              config={measureOneVm.measureOneConfig}
              onAddDataSource={measureOneVm.connectJobTaxDataImport}
              onTokenExpired={measureOneVm.generateAccessToken}
            />
          </WizardContentBody>

          {measureOneVm.canShowBottomBar && (
            <WizardBottomBar progress={0}>
              <WizardBottomBarSpacer />
              <WizardBottomBarSection>
                <ButtonGroup>
                  <Button
                    to={links.measureOneSelectExperienceType(vm.job.id)}
                    variant="outline"
                    label="Add another account"
                  />
                  <WizardBottomBarPrimaryButton
                    isDisabled={!measureOneVm.canNavigateToImportScreen}
                    to={links.measureOneSelectDocuments(vm.job.id)}
                    label="Select my documents"
                  />
                </ButtonGroup>
              </WizardBottomBarSection>
            </WizardBottomBar>
          )}
        </WizardPageAnimatedContent>
      </WizardPageSingleColumnRow>
    </>
  )
})

interface ConnectionBodyProps {
  jobId: string
  config: string
  onAddDataSource: (dataSourceId: string, dataSourceName: string) => void
  onTokenExpired: () => void
}

function ConnectionBody({
  config,
  jobId,
  onAddDataSource,
  onTokenExpired,
}: ConnectionBodyProps) {
  return (
    <Suspense fallback={<MeasureOneSpinner />}>
      <MeasureOneWidget
        onTokenExpired={onTokenExpired}
        jobId={Id.assert(jobId)}
        config={config}
        addDataSource={onAddDataSource}
      />
    </Suspense>
  )
}
